import React, { useState, useRef, useEffect } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { api } from "../services/api";
import MicTestBox from "../components/MicTestBox";
import AIAvatar from "../components/PitchSession/AIAvatar";
import Dialogs from "../components/PitchSession/Dialogs";
import SessionAlertDialog from "../components/PitchSession/SessionAlertDialog";
import SessionControls from "../components/PitchSession/SessionControls";
import AppLoader from "../components/AppLoader";
import { motion } from "framer-motion";
import useSpeechToText from "../hooks/useSpeechToText";
import useMicrophoneStatus from "../hooks/useMicrophoneStatus";
import PitchSession_Preview from "../components/PitchSession/PitchSession_Preview";
import AutoEvaluationDialog from "../components/AutoEvaluationDialog";

import { debugLog } from "../services/debugLog";

const PROCESSING_PHASES = [
  { status: "processing-warm", minMs: 3000, maxMs: 4000 },
  { status: "processing-thinking", minMs: 3000, maxMs: 4000 },
];

export default function PitchSession() {
  const { t, i18n } = useTranslation("pitch");
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { loading, settings } = useSettings();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const { pitchType, aiProfile } = location.state || {};
  const lang = i18n.language.startsWith("es") ? "es" : "en";
  const lang_STT = lang === "es" ? "es-ES" : "en-US";
  const skipMicTest = settings?.skip_mic_test === true;
  const useWebSpeech = settings?.use_webspeech === true;

  const sttEngine = settings?.stt_engine || "google";
  let maxRecordingDuration = null;
  if (!useWebSpeech) {
    maxRecordingDuration = 3 * 60 * 1000; // 3 minutos para cualquier motor STT backend
    // maxRecordingDuration = 20 * 1000; // 30seg para test
    // if (sttEngine === "google") {
    //   maxRecordingDuration = 59000; // 59 segundos
    // } elif:{
    //   maxRecordingDuration = 5 * 60 * 1000; // 5 minutos
    // }
  }

  const [isMicOn, setIsMicOn] = useState(false);
  const [avatarStatus, setAvatarStatus] = useState("idle");
  const [aiSpeaking, setAiSpeaking] = useState(false);
  const [processingStatusText, setProcessingStatusText] = useState("");
  const [userTranscript, setUserTranscript] = useState("");
  const [hasShownIntroCC, setHasShownIntroCC] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [ttsEnabled, setTTSEnabled] = useState(true);
  const [showCC, setShowCC] = useState(false);
  const [showStartButton, setShowStartButton] = useState(true);
  const [isStartingSession, setIsStartingSession] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [showInterior, setShowInterior] = useState(false);
  const [showInitialFeedback, setShowInitialFeedback] = useState(false);
  const [recordingTimeLeftMs, setRecordingTimeLeftMs] = useState(null);
  const [showEndDialog, setShowEndDialog] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [showEarlyPitchersPrompt, setShowEarlyPitchersPrompt] = useState(false);

  const [showMicPrompt, setShowMicPrompt] = useState(false);
  const [showNoAccessDialog, setShowNoAccessDialog] = useState(false);
  const [showNoSessionsDialog, setShowNoSessionsDialog] = useState(false);
  const [showActiveSessionDialog, setShowActiveSessionDialog] = useState(false);
  const [showBlockedOptionDialog, setShowBlockedOptionDialog] = useState(false);
  const [blockedOptionMessage, setBlockedOptionMessage] = useState("");
  const [activeSession, setActiveSession] = useState(null);
  const [closingActiveSession, setClosingActiveSession] = useState(false);
  const [showSelfEvalDialog, setShowSelfEvalDialog] = useState(false);
  const [endedSessionId, setEndedSessionId] = useState(null);

  const [previewZoomed, setPreviewZoomed] = useState(false);
  const [showBackgroundStarted, setShowBackgroundStarted] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [delayedReady, setDelayedReady] = useState(false);

  const [sessionId, setSessionId] = useState(null);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [endingSession, setEndingSession] = useState(false);
  const [previewBgUrl, setPreviewBgUrl] = useState("");
  const [startedBgUrl, setStartedBgUrl] = useState("");

  const toastTimeoutRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const recordingTimeoutRef = useRef(null);
  const recordingCountdownIntervalRef = useRef(null);
  const processingTimeoutsRef = useRef([]);

  const micStatus = useMicrophoneStatus();
  const isAvailable = skipMicTest ? true : micStatus.isAvailable;
  const permission = skipMicTest ? "granted" : micStatus.permission;
  const micReadyFinal = isAvailable && permission === "granted";

  const [mustEndSession, setMustEndSession] = useState(false); // Indica que la sesión debe terminarse, deshabilita controles (mic, audio y CC)
  const controlsDisabled = mustEndSession; // para pasar a SessionControls

  const clearProcessingTimeouts = () => {
    processingTimeoutsRef.current.forEach((timeoutId) => clearTimeout(timeoutId));
    processingTimeoutsRef.current = [];
  };

  const clearRecordingCountdown = () => {
    if (recordingCountdownIntervalRef.current) {
      clearInterval(recordingCountdownIntervalRef.current);
      recordingCountdownIntervalRef.current = null;
    }
    setRecordingTimeLeftMs(null);
  };

  const getRandomPhaseDuration = (minMs, maxMs) =>
    Math.floor(Math.random() * (maxMs - minMs + 1)) + minMs;

  const getProcessingStatusText = (status) => {
    if (lang === "es") {
      if (status === "processing-warm") return "Procesando audio...";
      if (status === "processing-thinking") return "Pensando...";
      if (status === "processing-ready") return "Preparando respuesta...";
      return "";
    }

    if (status === "processing-warm") return "Processing audio...";
    if (status === "processing-thinking") return "Thinking...";
    if (status === "processing-ready") return "Preparing response...";
    return "";
  };

  const setProcessingPhase = (status) => {
    setAvatarStatus(status);
    setProcessingStatusText(getProcessingStatusText(status));
  };

  const startProcessingFeedback = () => {
    clearProcessingTimeouts();
    setProcessingPhase(PROCESSING_PHASES[0].status);

    let accumulatedDelay = 0;

    PROCESSING_PHASES.forEach((phase, index) => {
      accumulatedDelay += getRandomPhaseDuration(phase.minMs, phase.maxMs);
      const nextStatus = PROCESSING_PHASES[index + 1]?.status || "processing-ready";

      const timeoutId = setTimeout(() => {
        setProcessingPhase(nextStatus);
      }, accumulatedDelay);

      processingTimeoutsRef.current.push(timeoutId);
    });
  };

  const stopProcessingFeedback = () => {
    clearProcessingTimeouts();
    setProcessingStatusText("");
  };


  const startPitchSession = async (data) => {
    const token = localStorage.getItem("token");
    const res = await api.post("/api/pitch-sessions/start", data);
    return res.data;
  };

  const endPitchSession = async (sessionId) => {
    // const token = localStorage.getItem("token");
    // debugLog("📡 Ending session", sessionId);
    // debugLog("🔐 Using token:", token?.slice(0, 20));
    const res = await api.post(`/api/pitch-sessions/${sessionId}/end`);
    return res.data;
  };

  const closeActiveSession = async () => {
    if (!activeSession?.id || closingActiveSession) return;

    setClosingActiveSession(true);
    try {
      await api.post(`/api/pitch-sessions/${activeSession.id}/close-active`);
      setShowActiveSessionDialog(false);
    } catch (err) {
      debugLog("❌ Error closing active session:", err);
      showToastMessage(t("error_starting_session"));
    } finally {
      setClosingActiveSession(false);
    }
  };

  const sendUserInteraction = async ({ sessionId, audioBlob, text, audioDurationMs = 0 }) => {
      if (text) {
        // 📤 Modo texto (Web Speech API)
        const res = await api.post("/api/pitch-interactions/", {
          pitch_session_id: sessionId,
          role: "user",
          content: text
        });
        return res.data;
      }

      // 🎙️ Modo audio
      const formData = new FormData();
      formData.append("audio", audioBlob, "audio.webm");
      formData.append("audio_duration_ms", audioDurationMs);
      formData.append("pitch_session_id", sessionId);
      formData.append("role", "user");

      const res = await api.post("/api/pitch-interactions/", formData);
      return res.data;
  };



  const showToastMessage = (message, duration = 4000) => {
    setToastMessage(message);
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    toastTimeoutRef.current = setTimeout(() => {
      setToastMessage("");
      toastTimeoutRef.current = null;
    }, duration);
  };

  const toggleTTS = () => {
    const newTTS = !ttsEnabled;
    setTTSEnabled(newTTS);
    const aiName = aiProfile?.[lang === "es" ? "name_es" : "name_en"];

    if (!newTTS && !showCC) {
      setShowCC(true);
      showToastMessage(`⚠️📝 ${t("auto_enabled_cc", { name: aiName })}`, 5000);
      return;
    }

    const message = newTTS
      ? `🔈 ${t("tts_enabled", { name: aiName })}`
      : `🔈 ${t("tts_disabled", { name: aiName })}`;
    showToastMessage(message, 3000);
  };

  const toggleCC = () => {
    const newCC = !showCC;
    setShowCC(newCC);
    const aiName = aiProfile?.[lang === "es" ? "name_es" : "name_en"];

    if (!newCC && !ttsEnabled) {
      setTTSEnabled(true);
      showToastMessage(`⚠️🔈 ${t("auto_enabled_tts", { name: aiName })}`, 5000);
    } else {
      const message = newCC
        ? `📝 ${t("cc_enabled")}`
        : `📝 ${t("cc_disabled")}`;
      showToastMessage(message, 3000);
    }
  };
  // auto evaluacion -----------------------
  const waitForSessionIdAndNavigate = async () => {
    let retries = 0;
    while (!endedSessionId && retries < 50) {       // Espera hasya 10 segundos
      console.log("⏳ Waiting for sessionId...");
      await new Promise((res) => setTimeout(res, 200)); // 200ms
      retries++;
    }
    if (endedSessionId)
      {
        console.log (`NAVIGATE/evaluation/${endedSessionId}`);
        navigate(`/evaluation/${endedSessionId}`);
      }
    else
      {
        console.log ("waitForSessionIdAndNavigate");
        navigate("/"); // fallback si hubo error
      }
  };

  const handleSubmitSelfEval = async ({ rating, comment }) => {
    try {
      if (endedSessionId) {
        await api.post(`/api/pitch-sessions/${endedSessionId}/self-eval`, {
          rating,
          comment,
        });
      }
    } catch (error) {
      console.error("❌ Error saving self-evaluation", error);
    } finally {
      await waitForSessionIdAndNavigate();
    }
  };

  const handleUserInteraction = async (
      audioOrText,
      durationMs,
      lang_STT,
      ttsEnabled,
      setUserTranscript,
      setAvatarStatus,
      setAiSpeaking,
      useWebSpeech,
      showToastMessage
    ) => {
      if (!sessionId || (!audioOrText && !useWebSpeech)) return;
      if (mustEndSession) return; // ⛔ Si la sesión debe terminarse, no hacemos nada

      const resetAvatar = () => {
        stopProcessingFeedback();
        setAiSpeaking(false);
        setAvatarStatus("idle");
      };
      startProcessingFeedback();
      setAiSpeaking(true);

      try {
        let data;
        if (useWebSpeech) {
          const text = audioOrText; // porque en este caso `audioBlob` es un string
          debugLog("📤 Enviando texto al backend:", text)
          data = await sendUserInteraction({ sessionId, text });
        } else {
          const audioBlob = audioOrText;
          debugLog("tamano audioBlob:", audioBlob.size);

          if (audioBlob.size < 15000) {  // por ejemplo, menos de 15kb
            console.warn("⚠️ Audio demasiado corto, no se envía.");
            if (showToastMessage) {
              showToastMessage(`⚠️ ${t("audio_too_short")}`, 3000);
            }

            resetAvatar();
            // setAiSpeaking(false);
            // setAvatarStatus("idle");
            setUserTranscript(t("ai_feedback"));

            return;
          }
          debugLog(`⏱️ SEND AUDIO (${(durationMs / 1000).toFixed(2)}s recorded)`);
          const interactionStartedAt = performance.now();
          data = await sendUserInteraction({ sessionId, audioBlob, audioDurationMs: durationMs });
          debugLog(`⏱️ BACKEND RESPONSE RECEIVED in ${((performance.now() - interactionStartedAt) / 1000).toFixed(2)}s`);
        }

        if (data.must_end_session) {
          setMustEndSession(true);
        }

        if (data.reply) {
          setUserTranscript(data.reply);
          // debugLog("audio_url:",data.audio_url); // Cheque nombre archivo de audio devuelto por el backend

          if (ttsEnabled && data.audio_url) {
            stopProcessingFeedback();
            setAvatarStatus("speaking");
            const audioStartedAt = performance.now();
            const audio = new Audio(data.audio_url);
            audio.onloadeddata = () => {
              debugLog(`⏱️ AUDIO LOADED in ${((performance.now() - audioStartedAt) / 1000).toFixed(2)}s`);
            };
            audio.oncanplaythrough = () => {
              debugLog(`⏱️ AUDIO CAN PLAY in ${((performance.now() - audioStartedAt) / 1000).toFixed(2)}s`);
            };
            audio.onplaying = () => {
              debugLog(`⏱️ AUDIO STARTED in ${((performance.now() - audioStartedAt) / 1000).toFixed(2)}s`);
            };
            audio.onended = () => {
              resetAvatar();
              // setAiSpeaking(false);
              // setAvatarStatus("idle");
            };
            debugLog("⏱️ AUDIO PLAY REQUESTED");
            audio.play();
          } else if (ttsEnabled) {
            stopProcessingFeedback();
            setAvatarStatus("speaking");
            const utterance = new SpeechSynthesisUtterance(data.reply);
            utterance.lang = lang_STT;
            utterance.onend = () => {
              resetAvatar();
              // setAiSpeaking(false);
              // setAvatarStatus("idle");
            };
            window.speechSynthesis.speak(utterance);
          } else {
            resetAvatar();
            // setAiSpeaking(false);
            // setAvatarStatus("idle");
          }
        }
      } catch (err) {
        const status = err?.response?.status;
        const data = err?.response?.data;
        
        debugLog("❌ handleUserInteraction error", { status, data, err: String(err) });
        
        if (status === 401) return;

        // Mensaje x límite de interacciones
        if (status === 409 && data?.must_end_session) {
          setMustEndSession(true);
          setAiSpeaking(false);
          setUserTranscript(t("session_max_interactions_reached"));
          setShowEndDialog(true); // 👈 abre el confirm para que el user cierre
          resetAvatar();
          return;
        }

        // Mostrar mensaje si no se detectó voz
        if (data?.error?.includes("No voice")) {
          console.warn("⚠️ Warning in handleUserInteraction (No Voice):", err);
          if (showToastMessage) {
            showToastMessage(`⚠️ ${t("no_voice_detected")}`, 3000);
          }
        } else {
          debugLog("❌ Err or in handleUserInteraction:", err);
        }

        resetAvatar();
        // setAiSpeaking(false);
        // setAvatarStatus("idle");
        setUserTranscript(t("ai_feedback"));

      }
    };

  const handleCancelSelfEval = async () => {
    await waitForSessionIdAndNavigate();
  };
  // auto evaluacion end -----------------------

  const confirmEnd = () => {
    if (!sessionId) return; // ⛔ Protección

    setEndingSession(true); // muestra loader

    // Oculta todo
    setShowControls(false);
    setShowInterior(false);
    setShowInitialFeedback(false);
    setShowBackgroundStarted(false);
    setPreviewZoomed(false);
    setUserTranscript("");
    setShowCC(false);

    // 🕓 Cierre de sesión en segundo plano
  endPitchSession(sessionId).then((res) => {
      const id = res?.session_id;
      const tooShort = res?.too_short === true;

      if (id) setEndedSessionId(id);

      if (tooShort) {
        // navigate(`/evaluation/${id}?nedte=true`);
        navigate(`/evaluation/0?nedte=true`);
      } else {
        setShowSelfEvalDialog(true);
      }

      setEndingSession(false);
    });
  };

  const onUserSpoken = async (transcript) => {
    if (!transcript?.trim()) return;
    stopListening();
    setIsMicOn(false);
    setShowInitialFeedback(false);

    // OJO hay 2 fucniones handleUserInteraction, esta para webspeech y otra para STT enviado al backend
    await handleUserInteraction(
      transcript,
      0, // durationMs no aplica para webspeech
      lang_STT,
      ttsEnabled,
      setUserTranscript,
      setAvatarStatus,
      setAiSpeaking,
      useWebSpeech,
      showToastMessage
    );

    setHasShownIntroCC(true); // 👈 esto evita que se muestre de nuevo el mensaje inicial
  };

  const { startListening, stopListening } = useSpeechToText({
    lang: lang_STT,
    onResult: onUserSpoken,
    // onResult: skipMicTest ? () => {} : onUserSpoken,
  });

  const toggleRecording = () => {
    // Si la grabación está activa, la detenemos; si no, la iniciamos
    debugLog("🎛️ toggleRecording", { isMicOn, useWebSpeech, sttEngine });
    if (isMicOn) {
      // Detener grabación/escucha
      if (useWebSpeech) stopListening(); // Si usamos Web Speech, paramos el reconocimiento
      else stopRecording(); // si usamos MediaRecorder, paramos la grabación
      // Visualmente apagamos el micrófono
      setIsMicOn(false);
    } else {
      // Iniciar grabación/escucha
      if (useWebSpeech) startListening(); // usa webspeech
      else startRecording(); // usa MediaRecorder, y manda audio al backend
      // Visualmente encendemos el micrófono
      setIsMicOn(true);
    }
  };

  const startVisualSequence = async () => {
    if (isStartingSession) return;
    setIsStartingSession(true);

    if (!isAuthenticated) {
      setIsStartingSession(false);
      return setShowLoginPrompt(true);
    }
    const isUndeltaUser = user?.source === "undelta";


    // 🚀 Mostrar popup Early Pitchers si corresponde
    if (user && user.is_early_pitcher === false && !isUndeltaUser) {
      setIsStartingSession(false);
      setShowEarlyPitchersPrompt(true);
      return; // Evita iniciar sesión hasta que cierre el popup
    }

    if (!user?.can_operate) {
      setIsStartingSession(false);
      setShowNoAccessDialog(true);
      return;
    }

    // 🔄 Verificar sesiones disponibles (API)
    try {
      const res = await api.get("/api/pitch-sessions/availability");
      const data = res.data;

      if (data.available_sessions < 1) {
        setIsStartingSession(false);
        setShowNoSessionsDialog(true);
        return;
      }
    } catch (err) {
      console.error("❌ Error checking availability", err);
      showToastMessage("Error al verificar tus sesiones disponibles.");
      setIsStartingSession(false);
      return;
    }

    if (!micReadyFinal && !skipMicTest) {
      setIsStartingSession(false);
      return setShowMicPrompt(true);
    }


    const handleStart = async () => {
      if (!pitchType || !aiProfile) {
        debugLog("❌ Cannot start session: pitchType or aiProfile is missing.");
        return false;
      }

      try {
        const res = await startPitchSession({
          pitch_type_id: pitchType.id,
          ai_profile_id: aiProfile.id,
          language: i18n.language,
        });

        setSessionId(res.session_id);

        setSessionStarted(true);
        // setSessionEnded(false);

        debugLog("✅ Session started:", res.session_id);
        return true;
      } catch (err) {
        debugLog("❌ Error starting session:", err);
        console.log("🔍 Response error:", err?.response);
        setIsStartingSession(false);
        if (err?.response?.status === 401) return false;
        if (err?.response?.status === 409 && err?.response?.data?.error === "active_session_exists") {
          setActiveSession(err.response.data.active_session);
          setShowActiveSessionDialog(true);
          return false;
        }
        if (err?.response?.status === 403 && err?.response?.data?.error === "practice_option_blocked") {
          const blockedOptions = err.response.data.blocked_options || [];
          const blockedNames = blockedOptions
            .map((option) => option[`name_${lang}`] || option.name_en)
            .filter(Boolean);
          setBlockedOptionMessage(t("blocked_option", { name: blockedNames.join(" + ") }));
          setShowBlockedOptionDialog(true);
          return false;
        }
        alert(t("error_starting_session") || "There was a problem starting the session.");
        return false;
      }
    };

    const started = await handleStart();
    if (!started) return; // ⛔ No seguimos con la UI si falló

    setShowStartButton(false);
    setIsStartingSession(false);
    setPreviewZoomed(true);

    setTimeout(() => {
      setShowBackgroundStarted(true);
      new Audio("/sounds/woosh.mp3").play();
    }, 1000);

    setTimeout(() => {
      setShowCC(true);
      setShowInterior(true);
      setShowInitialFeedback(true);
      setHasShownIntroCC(false);    // ✅ permite mostrarlo
      setUserTranscript(t("ai_feedback")); // 👈 mensaje inicial
      setShowControls(true);
    }, 1400);
  };

  const exitSession = () => {
    if (sessionStarted && sessionId) confirmEnd();
    navigate("/pitch-start", { state: { pitchType, aiProfile } });
  };


  const startRecording = async () => {
    // Esta función inicia la grabación de audio usando MediaRecorder
    try {
      debugLog("🎙️ Grabando audio...");
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";
      const mediaRecorder = new MediaRecorder(stream, { mimeType });

      recordedChunksRef.current = [];
      mediaRecorderRef.current = mediaRecorder;

      const startedAt = performance.now();
      mediaRecorder.onstart = () => {
        debugLog("🟢 MediaRecorder started", { mimeType });
      };
      mediaRecorder.onerror = (e) => {
        debugLog("❌ MediaRecorder error", e?.error || e);
      };

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          recordedChunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        if (recordingTimeoutRef.current) {
          clearTimeout(recordingTimeoutRef.current);
          recordingTimeoutRef.current = null;
        }
        clearRecordingCountdown();
        setIsMicOn(false);
        const durationMs = Math.round(performance.now() - startedAt);
        debugLog("🔴 Grabación detenida", {
          durationMs,
          chunks: recordedChunksRef.current.length,
        });
        // ✅ liberar mic SIEMPRE
        stream.getTracks().forEach((t) => t.stop());

        const audioBlob = new Blob(recordedChunksRef.current, { type: "audio/webm" });

        debugLog("🎧 audioBlob final", {
          size: audioBlob.size,
          type: audioBlob.type,
          durationMs,
        });

        setUserTranscript(`🕓 ${t("waiting_response", {
          name: aiProfile?.[lang === "es" ? "name_es" : "name_en"]
        })}`);

        // OJO hay 2 fucniones handleUserInteraction, esta para STT enviado al backend y otra para webspeech
        await handleUserInteraction(
          audioBlob,
          durationMs,
          lang_STT,
          ttsEnabled,
          setUserTranscript,
          setAvatarStatus,
          setAiSpeaking,
          useWebSpeech,
          showToastMessage
        );
      };

      mediaRecorder.start(); // ✅ sin timeslice (mejor para produccion)

      if (maxRecordingDuration !== null) {
        setRecordingTimeLeftMs(maxRecordingDuration);
        recordingCountdownIntervalRef.current = setInterval(() => {
          setRecordingTimeLeftMs((prev) => {
            if (prev === null) return null;
            const nextValue = prev - 1000;
            return nextValue > 0 ? nextValue : 0;
          });
        }, 1000);

        recordingTimeoutRef.current = setTimeout(() => {
          if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
            mediaRecorderRef.current.stop();
          }
        }, maxRecordingDuration);
      }

    } catch (err) {
      console.error("❌ Error al grabar audio:", err);
      setToastMessage("Error al activar el micrófono.");
    }
  };

  const stopRecording = () => {
    if (recordingTimeoutRef.current) {
      clearTimeout(recordingTimeoutRef.current);
      recordingTimeoutRef.current = null;
    }
    clearRecordingCountdown();

    if (mediaRecorderRef.current?.state === "recording") {
      debugLog("🛑 stopRecording() -> mediaRecorder.stop()");
      mediaRecorderRef.current.stop();
    } else {
      debugLog("⚠️ stopRecording() called but recorder not recording", {
        state: mediaRecorderRef.current?.state,
      });
    }
  };

  useEffect(() => {
    debugLog("Settings:", settings);
  }, [settings]);

  useEffect(() => () => {
    clearProcessingTimeouts();
    clearRecordingCountdown();
  }, []);


  useEffect(() => {
    if (!pitchType || !aiProfile) navigate("/", { replace: true });
  }, [pitchType, aiProfile, navigate]);

  const pitchTypeId = pitchType?.id || "default";
  const pitchName = pitchType?.[`name_${lang}`] || "Pitch";
  const aiName = aiProfile?.[`name_${lang}`];
  const title = aiName ? `${pitchName} ${t("with")} ${aiName}` : pitchName;

  useEffect(() => {
    setImagesLoaded(false);

    const previewUrl = `/images/pitch_types/session_${pitchTypeId}_preview.jpg`;
    const startedUrl = `/images/pitch_types/session_${pitchTypeId}_started.jpg`;

    const defaultPreviewUrl = "/images/pitch_types/session_default_preview.jpg";
    const defaultStartedUrl = "/images/pitch_types/session_default_started.jpg";

    const previewImg = new Image();
    const startedImg = new Image();

    let loadedCount = 0;

    const handleLoad = () => {
      loadedCount++;
      if (loadedCount === 2) setImagesLoaded(true);
    };

    previewImg.onload = () => {
      setPreviewBgUrl(previewUrl);
      handleLoad();
    };

    previewImg.onerror = () => {
      setPreviewBgUrl(defaultPreviewUrl);
      handleLoad();
    };

    startedImg.onload = () => {
      setStartedBgUrl(startedUrl);
      handleLoad();
    };

    startedImg.onerror = () => {
      setStartedBgUrl(defaultStartedUrl);
      handleLoad();
    };

    previewImg.src = previewUrl;
    startedImg.src = startedUrl;
  }, [pitchTypeId]);

  useEffect(() => {
    if (!loading && imagesLoaded) {
      const timeout = setTimeout(() => setDelayedReady(true), 100);
      return () => clearTimeout(timeout);
    }
  }, [loading, imagesLoaded]);

  useEffect(() => {
    const nav = document.querySelector(".main-navbar");
    const footer = document.querySelector(".main-footer");
    const html = document.documentElement;
    const body = document.body;
    if (nav) nav.style.display = "none";
    if (footer) footer.style.display = "none";
    if (html) html.style.overflow = "hidden";
    if (body) body.style.overflow = "hidden";
    return () => {
      if (nav) nav.style.display = "";
      if (footer) footer.style.display = "";
      if (html) html.style.overflow = "";
      if (body) body.style.overflow = "";
    };
  }, []);

  if (!pitchType || !aiProfile || !delayedReady) return <AppLoader />;
  if (endingSession) return <AppLoader message={t("evaluating_session")} />;

  return (
    <>
      {/* Fondo */}
      <Box sx={{ position: "fixed", inset: 0, zIndex: 0 }}>
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url('${previewBgUrl}')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            filter: previewZoomed ? "blur(6px) brightness(0.8)" : "none",
            transition: "filter 1.3s ease-out",
            opacity: showInterior ? 0 : 1,
            zIndex: 1,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url('${startedBgUrl}')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            transform: showBackgroundStarted ? "scale(1.02)" : "scale(1.2)",
            opacity: showBackgroundStarted ? 1 : 0,
            transition: "opacity 1s ease-in-out, transform 1.2s ease-out",
            zIndex: 2,
          }}
        />
      </Box>

      {/* Contenido */}
      <Box sx={{ position: "fixed", inset: 0, zIndex: 3, py: 4, px: 2, display: "flex", flexDirection: "column", justifyContent: showInterior ? "space-between" : "center" }}>
        <Box sx={{ position: "absolute", top: 16, left: 16 }}>
          <Typography variant="h6" fontWeight={600} sx={{ px: 2, py: 1, borderRadius: 2, backgroundColor: "rgba(0,0,0,0.5)", color: "#fff" }}>
            {title}
          </Typography>
        </Box>

        {showInterior && (
          <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }}>
            <AIAvatar
              aiProfile={aiProfile}
              userTranscript={userTranscript}
              processingStatusText={processingStatusText}
              showInitialFeedback={showInitialFeedback}
              hasShownIntroCC={true}
              showCC={showCC}
              aiSpeaking={aiSpeaking}
              recordingNearLimit={isMicOn && recordingTimeLeftMs !== null && recordingTimeLeftMs <= 15000}
              avatarStatus={avatarStatus}
              isMobile={isMobile}
              t={t}
              animateEntry={true}
            />
          </motion.div>
        )}

        {showMicPrompt && !skipMicTest && (
          <MicTestBox
            skipMicTest={skipMicTest}
            onContinue={() => setShowMicPrompt(false)}
            t={t}
          />
        )}

        {showStartButton && !showMicPrompt && (
          <PitchSession_Preview
            pitchType={pitchType}
            aiProfile={aiProfile}
            lang={i18n.language}
          />
        )}

        <SessionControls
          t={t}
          sessionStarted={sessionStarted}
          isMicOn={isMicOn}
          toggleRecording={toggleRecording}
          confirmEnd={confirmEnd}
          toggleCC={toggleCC}
          showCC={showCC}
          handleExit={exitSession}
          onStart={startVisualSequence}
          micReady={micReadyFinal}
          showMicPrompt={showMicPrompt}
          setShowMicPrompt={setShowMicPrompt}
          showStartButton={showStartButton}
          isStartingSession={isStartingSession}
          showControls={showControls}
          ttsEnabled={ttsEnabled}
          showEndDialog={showEndDialog}
          setShowEndDialog={setShowEndDialog}
          toggleTTS={toggleTTS}
          interactionInProgress={aiSpeaking}
          controlsDisabled={controlsDisabled}
          recordingTimeLeftMs={recordingTimeLeftMs}
        />

        <Dialogs
          t={t}
          showEndDialog={showEndDialog}
          setShowEndDialog={setShowEndDialog}
          handleEndConfirmed={confirmEnd}
          showLoginPrompt={showLoginPrompt}
          setShowLoginPrompt={setShowLoginPrompt}
          showEarlyPitchersPrompt={showEarlyPitchersPrompt}
          setShowEarlyPitchersPrompt={setShowEarlyPitchersPrompt}
          pitchType={pitchType}
          aiProfile={aiProfile}
        />

        <SessionAlertDialog
          open={showNoAccessDialog}
          onClose={() => setShowNoAccessDialog(false)}
          onConfirm={() => navigate("/contact?type=technical")}
          onCancel={() => setShowNoAccessDialog(false)}
          title={t("can_operate.no_access_title")}
          description={t("can_operate.no_access_description")}
          confirmText={t("can_operate.contact_support")}
          cancelText={t("can_operate.close")}
        />

        <SessionAlertDialog
          open={showNoSessionsDialog}
          onClose={() => setShowNoSessionsDialog(false)}
          onConfirm={() => navigate("/pricing")}
          onCancel={() => setShowNoSessionsDialog(false)}
          title={t("sessions_availability.no_sessions_title")}
          description={t("sessions_availability.no_sessions_description")}
          confirmText={t("sessions_availability.buy_sessions")}
          cancelText={t("sessions_availability.close")}
        />

        <SessionAlertDialog
          open={showActiveSessionDialog}
          onClose={() => setShowActiveSessionDialog(false)}
          onConfirm={closeActiveSession}
          onCancel={() => setShowActiveSessionDialog(false)}
          title={t("active_session_title")}
          description={t("active_session_description")}
          confirmText={closingActiveSession ? t("active_session_closing") : t("active_session_close")}
          cancelText={t("active_session_exit")}
          details={activeSession && (
            <Box sx={{ display: "grid", gap: 0.75, textAlign: "left" }}>
              <Box>
                <Typography component="span" sx={{ color: "rgba(255,255,255,0.68)", fontSize: 12, mr: 1 }}>
                  {t("active_session_started")}:
                </Typography>
                <Typography component="span" sx={{ fontSize: 14 }}>
                  {activeSession.start_time
                    ? new Intl.DateTimeFormat(lang === "es" ? "es-AR" : "en-US", {
                        dateStyle: "short",
                        timeStyle: "short",
                      }).format(new Date(activeSession.start_time))
                    : "-"}
                </Typography>
              </Box>
              <Box>
                <Typography component="span" sx={{ color: "rgba(255,255,255,0.68)", fontSize: 12, mr: 1 }}>
                  {t("active_session_pitch_type")}:
                </Typography>
                <Typography component="span" sx={{ fontSize: 14 }}>
                  {lang === "es" ? activeSession.pitch_type_name_es : activeSession.pitch_type_name_en}
                </Typography>
              </Box>
              <Box>
                <Typography component="span" sx={{ color: "rgba(255,255,255,0.68)", fontSize: 12, mr: 1 }}>
                  {t("active_session_ai_profile")}:
                </Typography>
                <Typography component="span" sx={{ fontSize: 14 }}>
                  {lang === "es" ? activeSession.ai_profile_name_es : activeSession.ai_profile_name_en}
                </Typography>
              </Box>
              <Box>
                <Typography component="span" sx={{ color: "rgba(255,255,255,0.68)", fontSize: 12, mr: 1 }}>
                  {t("active_session_language")}:
                </Typography>
                <Typography component="span" sx={{ fontSize: 14 }}>
                  {activeSession.language === "es" ? "Español" : "English"}
                </Typography>
              </Box>
            </Box>
          )}
        />

        <SessionAlertDialog
          open={showBlockedOptionDialog}
          onClose={() => setShowBlockedOptionDialog(false)}
          onConfirm={() => setShowBlockedOptionDialog(false)}
          title={t("blocked_title")}
          description={blockedOptionMessage}
          confirmText={t("blocked_close")}
        />


        {toastMessage && (
          <Typography
            variant="body2"
            sx={{
              position: "absolute",
              bottom: 16,
              left: "50%",
              transform: "translateX(-50%)",
              backgroundColor: "rgba(0,0,0,0.6)",
              color: "#fff",
              px: 1,
              py: 0.5,
              borderRadius: 2,
              fontSize: 12,
              zIndex: 9999,
              textAlign: "center",
              width: "fit-content",
              maxWidth: { xs: "90vw", sm: "70vw" },
              boxSizing: "border-box",
            }}
          >
            {toastMessage}
          </Typography>
        )}
      </Box>

      <AutoEvaluationDialog
        open={showSelfEvalDialog}
        onSubmit={handleSubmitSelfEval}
        onCancel={handleCancelSelfEval}
      />
    </>
  );
}
